import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { JobLogsDrawer } from '@/console/features/jobs/components/job-logs-drawer/job-logs-drawer';
import { useFeature } from '@/console/features/pipelines/hooks/use-feature/use-feature';
import { TConversionStatusProps } from '@/console/features/projects/components/conversion-status/conversion-status.types';
import { useConversionStatus } from '@/console/features/projects/hooks/use-conversion-status/use-conversion-status';
import { RefreshIcon } from '@/shared/assets/icons/refresh-icon';
import { Button } from '@/shared/components/ui/button/button';
import { Chip } from '@/shared/components/ui/chip/chip';
import { Spinner } from '@/shared/components/ui/spinner/spinner';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { FeatureEnum, JobStatusEnum } from '@/shared/types/types.generated';

export const ConversionStatus: React.FC<TConversionStatusProps> = ({
  projectId,
  versionId,
  vendorId,
  ideConfigId,
  projectType,
}) => {
  const { t } = useTranslation();
  const { isIdeSupported, projectTypesForFeature } = useFeature(
    FeatureEnum.ProjectConvert,
  );
  const isSupported =
    isIdeSupported(vendorId, ideConfigId) &&
    projectTypesForFeature.includes(projectType);
  const {
    jobId,
    status,
    isLoading,
    errorMessage,
    triggerConversion,
    isCreateConversionLoading,
  } = useConversionStatus({
    versionId,
    projectId,
    disabled: !isSupported,
  });
  const { getStatusColor } = useJobStatus();

  const _triggerConversion = async () => {
    await triggerConversion(projectId, versionId);
  };

  const getChipText = () => {
    if (isLoading) {
      return t('common:loading');
    }

    if (status) {
      return t(`pipelines:job_execution_status.${status.toLowerCase()}`);
    }

    return t('pipelines:no_executions');
  };

  const canTriggerConversion = () => {
    const hasFailedStatus =
      status === JobStatusEnum.Failed ||
      status === JobStatusEnum.TimedOut ||
      status === JobStatusEnum.Skipped ||
      !status;

    return hasFailedStatus && !isCreateConversionLoading;
  };

  const getLabel = () => {
    if (isCreateConversionLoading) {
      return (
        <Stack direction="row" alignItems="center" gap="0.375rem">
          <Spinner size="xs" />
          <Typography variant="button" textTransform="capitalize">
            {getChipText()}
          </Typography>
        </Stack>
      );
    }

    return getChipText();
  };

  const getRetryConversionButton = () => {
    if (!canTriggerConversion()) {
      return null;
    }

    return (
      <Button
        size="extraSmall"
        variant="secondary"
        onClick={_triggerConversion}
        icon={<RefreshIcon />}
        tooltip={t('pipelines:trigger_conversion')}
      >
        {t('common:retry')}
      </Button>
    );
  };

  if (!isSupported) {
    return '-';
  }

  return (
    <JobLogsDrawer
      jobId={jobId}
      trigger={
        <Chip
          tooltip={errorMessage}
          status={getStatusColor(status)}
          label={getLabel()}
        />
      }
      action={getRetryConversionButton()}
    />
  );
};
